'use client';

import { AnimatePresence } from 'framer-motion';
import Link from 'next/link';
import type { RefObject } from 'react';

import HeaderActiveDropdown from './HeaderActiveDropdown';
import {
  Badge,
  getBadgeDotClassName,
  getNavTriggerClassName,
  HEADER_CONTAINER_CLASS,
  NavigationChevron,
  NavLabel,
} from '../../lib/header-constants';
import type { DropdownPosition } from '../../types/header';
import { useNavbarContext } from './context';
import useHeaderOverflowMenu, {
  MORE_ITEM_LABEL,
  MORE_ITEM_MEASUREMENT_DROPDOWN,
} from '../../hooks/header/useHeaderOverflowMenu';
import { cn } from '@/shared/utils/cn';
import type { NavItem } from '../../types/navigation';

interface HeaderNavBarProps {
  dropdownPosition: DropdownPosition | null;
  dropdownRef: RefObject<HTMLDivElement | null>;
  navItems: NavItem[];
  openDropdown: string | null;
  openedItem: NavItem | null;
  onDropdownClose: () => void;
  onDropdownEnter: (key: string) => void;
  onDropdownLeave: () => void;
  onNavTriggerRef: (key: string, element: HTMLButtonElement | null) => void;
}

const renderBadge = (item: NavItem) => {
  if (!item.badge) {
    return null;
  }

  return (
    <span className="nav-item-badge">
      <span className="nav-item-badge-label" data-nav-badge-label={item.label}>
        <Badge className="nav-item-badge-pill" color={item.badgeColor!} label={item.badge} />
      </span>
      <span
        aria-hidden="true"
        className={cn('nav-item-badge-dot', getBadgeDotClassName(item.badgeColor))}
        data-nav-badge-dot={item.label}
      />
    </span>
  );
};

const HeaderNavBar = ({
  dropdownPosition,
  dropdownRef,
  navItems,
  openDropdown,
  openedItem,
  onDropdownClose,
  onDropdownEnter,
  onDropdownLeave,
  onNavTriggerRef,
}: HeaderNavBarProps) => {
  const { onNavClick } = useNavbarContext();
  const { containerRef, displayItems, measurementRef, moreItem } =
    useHeaderOverflowMenu(navItems);
  const activeDropdownItem = openDropdown === moreItem?.label ? moreItem : openedItem;

  const handleDropdownClick = (item: NavItem, isOpen: boolean) => {
    if (isOpen) {
      onDropdownClose();
      return;
    }

    onDropdownEnter(item.label);
  };

  const handleDropdownKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, item: NavItem) => {
    if (event.key !== 'ArrowDown') {
      return;
    }

    event.preventDefault();
    onDropdownEnter(item.label);
  };

  const setNavTriggerRef = (item: NavItem, element: HTMLButtonElement | null) => {
    onNavTriggerRef(item.label, element);
  };

  const renderNavItemContent = (item: NavItem, isOpen: boolean) => {
    const hasDropdown = Boolean(item.dropdown?.length || item.megaMenu);

    if (!hasDropdown) {
      return (
        <Link
          className={getNavTriggerClassName(Boolean(item.active), false, item.highlight)}
          href={item.href}
          prefetch={false}
          onClick={() => onNavClick?.({ href: item.href, label: item.label, url: item.href })}
        >
          <NavLabel isActive={Boolean(item.active)} label={item.label} />
          {renderBadge(item)}
        </Link>
      );
    }

    return (
      <button
        aria-expanded={isOpen}
        aria-haspopup={item.megaMenu ? 'dialog' : 'menu'}
        className={getNavTriggerClassName(Boolean(item.active), isOpen, item.highlight)}
        ref={(element) => setNavTriggerRef(item, element)}
        type="button"
        onClick={() => handleDropdownClick(item, isOpen)}
        onFocus={() => onDropdownEnter(item.label)}
        onKeyDown={(event) => handleDropdownKeyDown(event, item)}
      >
        <NavLabel isActive={Boolean(item.active) || isOpen} label={item.label} />
        {renderBadge(item)}
        <NavigationChevron isOpen={isOpen} />
      </button>
    );
  };

  const renderMeasuredNavItemContent = (item: NavItem) => {
    const hasDropdown = Boolean(item.dropdown?.length || item.megaMenu);

    if (!hasDropdown) {
      return (
        <span className={getNavTriggerClassName(Boolean(item.active), false, item.highlight)}>
          <NavLabel isActive={Boolean(item.active)} label={item.label} />
          {renderBadge(item)}
        </span>
      );
    }

    return (
      <span className={getNavTriggerClassName(Boolean(item.active), false, item.highlight)}>
        <NavLabel isActive={Boolean(item.active)} label={item.label} />
        {renderBadge(item)}
        <NavigationChevron isOpen={false} />
      </span>
    );
  };

  const renderMeasuredItem = (item: NavItem) => (
    <li
      className="relative shrink-0 first:-ml-2 last:-mr-2 xl:first:-ml-3.5 xl:last:-mr-3.5"
      data-nav-measure-item={item.label}
      key={item.label}
    >
      {renderMeasuredNavItemContent(item)}
    </li>
  );

  return (
    <>
      <nav aria-label="Navegación principal del sitio" className="hidden lg:block">
        <div
          className={cn(HEADER_CONTAINER_CLASS, 'global-header-nav-viewport flex py-1.5')}
          ref={containerRef}
        >
          <ul className="flex min-w-0 max-w-full items-center overflow-visible whitespace-nowrap">
            {displayItems.map((item) => {
              const hasDropdown = Boolean(item.dropdown?.length || item.megaMenu);
              const isOpen = openDropdown === item.label;

              return (
                <li
                  className="relative shrink-0 first:-ml-2 last:-mr-2 xl:first:-ml-3.5 xl:last:-mr-3.5"
                  key={item.label}
                  onMouseEnter={hasDropdown ? () => onDropdownEnter(item.label) : undefined}
                  onMouseLeave={hasDropdown ? onDropdownLeave : undefined}
                >
                  {renderNavItemContent(item, isOpen)}
                </li>
              );
            })}
          </ul>

          <ul className="global-header-nav-measurement" ref={measurementRef}>
            {navItems.map(renderMeasuredItem)}
            <li
              data-nav-measure-more
              className="relative shrink-0 first:-ml-2 last:-mr-2 xl:first:-ml-3.5 xl:last:-mr-3.5"
            >
              {renderMeasuredNavItemContent({
                dropdown: MORE_ITEM_MEASUREMENT_DROPDOWN,
                href: '#',
                label: MORE_ITEM_LABEL,
              })}
            </li>
          </ul>
        </div>
      </nav>

      <AnimatePresence>
        <HeaderActiveDropdown
          dropdownPosition={dropdownPosition}
          dropdownRef={dropdownRef}
          openDropdown={openDropdown}
          openedItem={activeDropdownItem}
          onDropdownEnter={onDropdownEnter}
          onDropdownLeave={onDropdownLeave}
        />
      </AnimatePresence>
    </>
  );
};

export default HeaderNavBar;
